import type { DateOptions } from '../../types/text-mode.types';

export interface DateRange {
  min: string;
  max: string;
}

function getToday(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addDays(date: Date, offset: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + offset);
  return next;
}

function formatDateInputValue(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function buildDateRangeFromOffsets(
  dateOptions?: DateOptions,
  now: Date = new Date(),
): DateRange | null {
  if (!dateOptions) {
    return null;
  }

  const today = getToday(now);
  const firstBoundary = addDays(today, dateOptions.minOffset);
  const secondBoundary = addDays(today, dateOptions.maxOffset);
  const [minDate, maxDate] = firstBoundary <= secondBoundary
    ? [firstBoundary, secondBoundary]
    : [secondBoundary, firstBoundary];

  return {
    min: formatDateInputValue(minDate),
    max: formatDateInputValue(maxDate),
  };
}

export function isDateWithinRange(value: string, dateRange: DateRange | null): boolean {
  if (!value || !dateRange) {
    return true;
  }

  return value >= dateRange.min && value <= dateRange.max;
}

export function formatDateRangeValue(value: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return value;
  }

  const year = Number.parseInt(match[1], 10);
  const monthIndex = Number.parseInt(match[2], 10) - 1;
  const day = Number.parseInt(match[3], 10);

  if (monthIndex < 0 || monthIndex > 11 || Number.isNaN(year) || Number.isNaN(day)) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' })
    .format(new Date(year, monthIndex, day));
}
